// client/src/components/configuracoes/entrega/EntregaConfigPage.js
import React, { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import { useEntregaConfigs, useMutateEntregaConfig } from '../../../hooks/useEntregaConfig';
import { useUnidades } from '../../../hooks/useUnidades';
import useAlert from '../../../hooks/useAlert';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';
import apiClient from '../../../services/api';
import EntregaForm from './EntregaForm';
import ActionsDrawer from '../../common/ActionsDrawer';
import {
    Truck, Edit, Trash2,
    Loader2, RefreshCcw, AlertCircle,
    CheckCircle2, XCircle, Filter as FilterIcon, X,
    PlusCircle, MoreVertical,
} from 'lucide-react';

// ============================================================
// CONSTANTES
// ============================================================
const DIAS = [
    { value: 0, label: 'Domingo' },
    { value: 1, label: 'Segunda-feira' },
    { value: 2, label: 'Terça-feira' },
    { value: 3, label: 'Quarta-feira' },
    { value: 4, label: 'Quinta-feira' },
    { value: 5, label: 'Sexta-feira' },
    { value: 6, label: 'Sábado' },
];

const DIAS_PADRAO = DIAS.map(d => ({
    diaSemana: d.value,
    horarioInicio: '08:00',
    horarioFim: '18:00',
    ativo: false,
}));

const TIPO_CALCULO_OPTIONS = [
    { value: 'raio',      label: 'Por Raio (km)' },
    { value: 'cep_range', label: 'Por Faixa de CEP' },
    { value: 'fixo',      label: 'Valor Fixo' },
];

const STATUS_BADGE = ({ ativo }) => (
    <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 10px', borderRadius: 99, fontSize: '0.8rem', fontWeight: 600,
        background: ativo ? '#dcfce7' : '#f1f5f9',
        color: ativo ? '#166534' : '#475569',
    }}>
        {ativo ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
        {ativo ? 'Ativo' : 'Inativo'}
    </span>
);

// ── builders ──────────────────────────────────────────────
const buildRetirada = (src) => ({
    ativo: src?.ativo ?? false,
    usarEnderecoUnidade: src?.usarEnderecoUnidade ?? true,
    enderecoCustom: {
        logradouro:   src?.enderecoCustom?.logradouro   || '',
        numero:       src?.enderecoCustom?.numero       || '',
        complemento:  src?.enderecoCustom?.complemento  || '',
        bairro:       src?.enderecoCustom?.bairro       || '',
        cidade:       src?.enderecoCustom?.cidade       || '',
        estado:       src?.enderecoCustom?.estado       || '',
        cep:          src?.enderecoCustom?.cep          || '',
    },
    prazoMinimoHoras: src?.prazoMinimoHoras ?? 0,
    diasDisponiveis: src?.diasDisponiveis?.length
        ? DIAS.map(d => {
            const found = src.diasDisponiveis.find(x => x.diaSemana === d.value);
            return found
                ? { diaSemana: d.value, horarioInicio: found.horarioInicio || '08:00', horarioFim: found.horarioFim || '18:00', ativo: found.ativo ?? false }
                : { diaSemana: d.value, horarioInicio: '08:00', horarioFim: '18:00', ativo: false };
        })
        : [...DIAS_PADRAO],
    instrucoes: src?.instrucoes || '',
});

const buildEntrega = (src) => ({
    ativo:          src?.ativo ?? false,
    tipoCalculo:    src?.tipoCalculo || 'raio',
    coordenadasBase: {
        latitude:  src?.coordenadasBase?.latitude  ?? '',
        longitude: src?.coordenadasBase?.longitude ?? '',
    },
    faixasRaio: src?.faixasRaio || [],
    faixasCep:  src?.faixasCep  || [],
    valorFixo:              src?.valorFixo              ?? 0,
    prazoFixoHoras:         src?.prazoFixoHoras         ?? 0,
    valorMinimoCompra:      src?.valorMinimoCompra      ?? 0,
    freteGratisMinimoCompra: src?.freteGratisMinimoCompra ?? '',
    instrucoes: src?.instrucoes || '',
});

const buildForm = (config) => ({
    unidade:  config?.unidade?._id || config?.unidade || '',
    retirada: buildRetirada(config?.retirada),
    entrega:  buildEntrega(config?.entrega),
});

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
const EntregaConfigPage = () => {
    const { setAlert } = useAlert();

    // ── estado UI ────────────────────────────────────────
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [showFilters,   setShowFilters]   = useState(false);
    const [current,       setCurrent]       = useState(null);
    const [currentStep,   setCurrentStep]   = useState(1);

    // ── estado de filtro ──────────────────────────────────
    const [filtroUnidade, setFiltroUnidade] = useState('');

    // ── estado do form ────────────────────────────────────
    const [formData,  setFormData]  = useState(buildForm(null));
    const [isSaving,  setIsSaving]  = useState(false);
    const [testeForm,    setTesteForm]    = useState({ cep: '', latitude: '', longitude: '', subtotal: '' });
    const [testeResultado, setTesteResultado] = useState(null);
    const [testando,       setTestando]       = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);

    // ── dados ─────────────────────────────────────────────
    const queryParams = useMemo(() => {
        const p = {};
        if (filtroUnidade) p.unidade = filtroUnidade;
        return p;
    }, [filtroUnidade]);

    const { data: configsData, isLoading: isLoadingConfig, error: configError, refetch } = useEntregaConfigs(queryParams);
    const { data: unidadesData, isLoading: isLoadingUnidades } = useUnidades({ ativo: true, limit: 100 });
    const { createConfig, updateConfig, deleteConfig } = useMutateEntregaConfig();

    const unidades = unidadesData?.data || [];
    const configs  = configsData?.data  || [];
    const isMutating = createConfig.isPending || updateConfig.isPending || deleteConfig.isPending;

    // ── helpers nome de unidade ───────────────────────────
    const nomeUnidade = useCallback((config) => {
        if (!config.unidade) return 'Padrão da conta';
        const u = config.unidade;
        if (typeof u === 'object') {
            return u.nome || 'Unidade';
        }
        const found = unidades.find(x => (x._id?.toString?.() || x._id) === u?.toString?.());
        return found?.nome || 'Unidade';
    }, [unidades]);

    const opcoesUnidade = useMemo(() =>
        unidades.map(u => ({
            uid: u._id?.toString?.() || u._id || u.id,
            label: `${u.nome}${u.localidade ? ` — ${u.localidade}` : u.cidade ? ` — ${u.cidade}` : ''}${u.uf ? `/${u.uf}` : ''}`,
        })),
    [unidades]);

    // ── sincroniza form quando current muda ───────────────
    useEffect(() => {
        setFormData(buildForm(current));
        setCurrentStep(1);
        setTesteResultado(null);
    }, [current?._id]);

    // ── abrir/fechar form ─────────────────────────────────
    const handleAddNew = useCallback(() => {
        setCurrent(null);
        setIsFormVisible(true);
    }, []);

    const handleEdit = useCallback((config) => {
        setCurrent(config);
        setIsFormVisible(true);
        setSelectedConfig(null);
    }, []);

    const handleCloseForm = useCallback(() => {
        setIsFormVisible(false);
        setCurrent(null);
    }, []);

    const handleDelete = useCallback((id) => {
        if (window.confirm('Tem certeza que deseja remover esta configuração?')) {
            deleteConfig.mutate(id, { onSuccess: () => setSelectedConfig(null) });
        }
    }, [deleteConfig]);

    // ── helpers de estado do form ─────────────────────────
    const setRetirada = useCallback((updater) => {
        setFormData(prev => ({
            ...prev,
            retirada: typeof updater === 'function' ? updater(prev.retirada) : { ...prev.retirada, ...updater },
        }));
    }, []);

    const setEntrega = useCallback((updater) => {
        setFormData(prev => ({
            ...prev,
            entrega: typeof updater === 'function' ? updater(prev.entrega) : { ...prev.entrega, ...updater },
        }));
    }, []);

    // ── faixas de raio ────────────────────────────────────
    const adicionarFaixaRaio = useCallback(() => {
        setEntrega(prev => ({ ...prev, faixasRaio: [...prev.faixasRaio, { raioKm: '', valor: 0, prazoHoras: 24, label: '', ativo: true }] }));
    }, [setEntrega]);

    const atualizarFaixaRaio = useCallback((idx, campo, valor) => {
        setEntrega(prev => ({ ...prev, faixasRaio: prev.faixasRaio.map((f, i) => i === idx ? { ...f, [campo]: valor } : f) }));
    }, [setEntrega]);

    const removerFaixaRaio = useCallback((idx) => {
        setEntrega(prev => ({ ...prev, faixasRaio: prev.faixasRaio.filter((_, i) => i !== idx) }));
    }, [setEntrega]);

    // ── faixas de cep ─────────────────────────────────────
    const adicionarFaixaCep = useCallback(() => {
        setEntrega(prev => ({ ...prev, faixasCep: [...prev.faixasCep, { cepInicio: '', cepFim: '', valor: 0, prazoHoras: 24, label: '', ativo: true }] }));
    }, [setEntrega]);

    const atualizarFaixaCep = useCallback((idx, campo, valor) => {
        setEntrega(prev => ({ ...prev, faixasCep: prev.faixasCep.map((f, i) => i === idx ? { ...f, [campo]: valor } : f) }));
    }, [setEntrega]);

    const removerFaixaCep = useCallback((idx) => {
        setEntrega(prev => ({ ...prev, faixasCep: prev.faixasCep.filter((_, i) => i !== idx) }));
    }, [setEntrega]);

    // ── dias disponíveis ──────────────────────────────────
    const atualizarDia = useCallback((idx, campo, valor) => {
        setRetirada(prev => ({ ...prev, diasDisponiveis: prev.diasDisponiveis.map((d, i) => i === idx ? { ...d, [campo]: valor } : d) }));
    }, [setRetirada]);

    // ── submit ────────────────────────────────────────────
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const payload = {
                retirada: {
                    ...formData.retirada,
                    prazoMinimoHoras: Number(formData.retirada.prazoMinimoHoras) || 0,
                },
                entrega: {
                    ...formData.entrega,
                    coordenadasBase: {
                        latitude:  formData.entrega.coordenadasBase.latitude  !== '' ? Number(formData.entrega.coordenadasBase.latitude)  : undefined,
                        longitude: formData.entrega.coordenadasBase.longitude !== '' ? Number(formData.entrega.coordenadasBase.longitude) : undefined,
                    },
                    valorFixo:              Number(formData.entrega.valorFixo)              || 0,
                    prazoFixoHoras:         Number(formData.entrega.prazoFixoHoras)         || 0,
                    valorMinimoCompra:      Number(formData.entrega.valorMinimoCompra)      || 0,
                    freteGratisMinimoCompra: formData.entrega.freteGratisMinimoCompra !== '' ? Number(formData.entrega.freteGratisMinimoCompra) : null,
                    faixasRaio: formData.entrega.faixasRaio.map(f => ({ ...f, raioKm: Number(f.raioKm) || 0, valor: Number(f.valor) || 0, prazoHoras: Number(f.prazoHoras) || 0 })),
                    faixasCep:  formData.entrega.faixasCep.map(f => ({ ...f, valor: Number(f.valor) || 0, prazoHoras: Number(f.prazoHoras) || 0 })),
                },
            };

            if (formData.unidade) payload.unidade = formData.unidade;

            if (current?._id) {
                await updateConfig.mutateAsync({ id: current._id, updatedData: payload });
            } else {
                await createConfig.mutateAsync(payload);
            }
            handleCloseForm();
        } catch (_) {
            // alert exibido pelo hook
        } finally {
            setIsSaving(false);
        }
    }, [formData, current, updateConfig, createConfig, handleCloseForm]);

    // ── testar cálculo ────────────────────────────────────
    const handleTestarCalculo = useCallback(async (e) => {
        e.preventDefault();
        if (!current?._id) {
            setAlert('Salve a configuração antes de testar.', 'danger');
            return;
        }
        setTestando(true);
        setTesteResultado(null);
        try {
            const body = {};
            if (testeForm.cep)       body.cep       = testeForm.cep;
            if (testeForm.latitude)  body.latitude   = Number(testeForm.latitude);
            if (testeForm.longitude) body.longitude  = Number(testeForm.longitude);
            if (testeForm.subtotal)  body.subtotal   = Number(testeForm.subtotal);
            const { data } = await apiClient.post(`/entrega-config/${current._id}/calcular`, body);
            setTesteResultado(data?.data || data);
        } catch (err) {
            setAlert(err.response?.data?.error || 'Erro ao calcular frete', 'danger');
        } finally {
            setTestando(false);
        }
    }, [current, testeForm, setAlert]);

    const hasFilters = !!filtroUnidade;

    // ── render ────────────────────────────────────────────
    if (configError) {
        return (
            <div className="page-management-container">
                <div className="table-empty-state">
                    <AlertCircle size={48} />
                    <h3>Erro ao carregar configurações</h3>
                    <p>{configError.message || 'Tente novamente.'}</p>
                    <button onClick={() => refetch()} className="action-button action-button-primary" style={{ marginTop: '1rem' }}>
                        <RefreshCcw size={18} className="icon" /> Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            {/* ── PAGE-MANAGEMENT-CONTAINER ── */}
            <div className="page-management-container">
                <PageHeader
                    icon={<Truck />}
                    title="Entrega e Retirada"
                    subtitle={isLoadingConfig ? null : `${configs.length} configuração${configs.length !== 1 ? 'ões' : ''}`}
                    actions={<>
                        {!isFormVisible && (
                            <>
                                <button
                                    onClick={() => setShowFilters(p => !p)}
                                    className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                                    disabled={isLoadingConfig}
                                >
                                    <FilterIcon size={18} className="icon" />
                                    {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                                </button>
                            </>
                        )}
                        {isFormVisible && (
                            <button onClick={handleCloseForm} className="action-button action-button-secondary">
                                <XCircle size={18} className="icon" /> Fechar
                            </button>
                        )}
                    </>}
                />

                {/* ── PAINEL FILTROS ── */}
                {showFilters && !isFormVisible && (
                    <div className="content-panel">
                        <h3 className="panel-title">Filtrar Configurações</h3>
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label className="form-label">Unidade</label>
                                {isLoadingUnidades ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: '0.875rem' }}>
                                        <Loader2 size={16} className="animate-spin" /> Carregando...
                                    </div>
                                ) : (
                                    <select
                                        className="form-input"
                                        value={filtroUnidade}
                                        onChange={e => setFiltroUnidade(e.target.value)}
                                    >
                                        <option value="">Todas as unidades</option>
                                        {opcoesUnidade.map(u => (
                                            <option key={u.uid} value={u.uid}>{u.label}</option>
                                        ))}
                                    </select>
                                )}
                            </div>
                        </div>
                        {hasFilters && (
                            <div style={{ marginTop: '0.75rem' }}>
                                <button
                                    type="button"
                                    onClick={() => setFiltroUnidade('')}
                                    className="action-button action-button-secondary"
                                >
                                    <X size={16} className="icon" /> Limpar Filtros
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* ── PAINEL FORMULÁRIO ── */}
                {isFormVisible && (
                    <div className="content-panel">
                        <EntregaForm
                            current={current}
                            formData={formData}
                            setFormData={setFormData}
                            opcoesUnidade={opcoesUnidade}
                            currentStep={currentStep}
                            setCurrentStep={setCurrentStep}
                            setRetirada={setRetirada}
                            setEntrega={setEntrega}
                            atualizarDia={atualizarDia}
                            adicionarFaixaRaio={adicionarFaixaRaio}
                            atualizarFaixaRaio={atualizarFaixaRaio}
                            removerFaixaRaio={removerFaixaRaio}
                            adicionarFaixaCep={adicionarFaixaCep}
                            atualizarFaixaCep={atualizarFaixaCep}
                            removerFaixaCep={removerFaixaCep}
                            isSaving={isSaving}
                            onSubmit={handleSubmit}
                            onCancel={handleCloseForm}
                            testeForm={testeForm}
                            setTesteForm={setTesteForm}
                            testeResultado={testeResultado}
                            testando={testando}
                            onTestar={handleTestarCalculo}
                        />
                    </div>
                )}
            </div>

            {/* ── TABLE-CONTAINER (fora do page-management-container) ── */}
            {!isFormVisible && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {isLoadingConfig ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin" />
                                <span>Carregando configurações...</span>
                            </div>
                        ) : configs.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th>Unidade</th>
                                        <th>Retirada</th>
                                        <th>Entrega</th>
                                        <th>Tipo de Cálculo</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {configs.map(cfg => (
                                        <tr key={cfg._id}>
                                            <td>
                                                <strong>{nomeUnidade(cfg)}</strong>
                                            </td>
                                            <td><STATUS_BADGE ativo={cfg.retirada?.ativo} /></td>
                                            <td><STATUS_BADGE ativo={cfg.entrega?.ativo} /></td>
                                            <td>
                                                {cfg.entrega?.ativo
                                                    ? TIPO_CALCULO_OPTIONS.find(o => o.value === cfg.entrega?.tipoCalculo)?.label || '—'
                                                    : <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>—</span>
                                                }
                                            </td>
                                            <td className="text-right">
                                                <button
                                                    onClick={() => setSelectedConfig(cfg)}
                                                    className="action-button-icon text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title="Ações"
                                                    disabled={isMutating}
                                                >
                                                    <MoreVertical size={18} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <Truck size={48} />
                                <h3>Nenhuma configuração encontrada</h3>
                                <p>
                                    {hasFilters
                                        ? 'Nenhuma configuração para a unidade selecionada.'
                                        : 'Crie a primeira configuração de entrega e retirada.'}
                                </p>
                                <button
                                    onClick={handleAddNew}
                                    className="action-button action-button-primary"
                                    style={{ marginTop: '1rem' }}
                                >
                                    <PlusCircle size={18} className="icon" /> Nova Configuração
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <FabButton
                onClick={handleAddNew}
                title="Nova Configuração"
                visible={!isFormVisible}
                disabled={isLoadingConfig}
                icon={<Truck size={26} className="icon" />}
            />

            <ActionsDrawer
                open={!!selectedConfig}
                onClose={() => setSelectedConfig(null)}
                title={selectedConfig ? nomeUnidade(selectedConfig) : 'Configuração'}
                actions={selectedConfig ? [
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <Edit size={20} className="icon" />,
                        onClick: () => handleEdit(selectedConfig),
                    },
                    {
                        key: 'excluir',
                        label: 'Remover',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedConfig._id),
                        destructive: true,
                        loading: isMutating,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default EntregaConfigPage;
